import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UserPlus } from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";

import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from "@/components";
import { useToast } from "@/components/ui/use-toast";

import * as clientsApi from "../../clients/clients.api";
import { TClientExtended } from "../../clients/clients.types";
import * as projectsApi from "../projects.api";

type AssociateSubPartnersModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  projectId: string;
  alreadyLinkedIds: string[];
};

export function AssociateSubPartnersModal({
  open,
  onOpenChange,
  clientId,
  projectId,
  alreadyLinkedIds,
}: AssociateSubPartnersModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = React.useState("");
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);

  const { data: subPartners = [], isLoading } = useQuery<TClientExtended[]>({
    queryKey: ["sub-partners", clientId],
    queryFn: () => clientsApi.getSubPartners(clientId),
    enabled: open && !!clientId,
    staleTime: 60_000,
  });

  const linkedSet = React.useMemo(
    () => new Set(alreadyLinkedIds),
    [alreadyLinkedIds],
  );

  const available = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return subPartners
      .filter((sp) => !linkedSet.has(sp.id))
      .filter((sp) => {
        if (!q) return true;
        const name = (sp.name ?? "").toLowerCase();
        const doc = (sp.cnpj ?? (sp as { cpf?: string }).cpf ?? "").toLowerCase();
        return name.includes(q) || doc.includes(q);
      })
      .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", "pt-BR"));
  }, [subPartners, linkedSet, search]);

  React.useEffect(() => {
    if (!open) {
      setSelectedIds([]);
      setSearch("");
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: (organizationIds: string[]) =>
      projectsApi.associateOrganizationParticipants(projectId, organizationIds),
    onSuccess: (result: {
      added: string[];
      already_linked: string[];
    }) => {
      queryClient.invalidateQueries({ queryKey: ["project", projectId] });
      toast({
        title: "Sub-parceiros associados",
        description:
          result.added.length > 0
            ? `${result.added.length} vinculado(s) ao projeto.`
            : "Nenhum novo vínculo (já estavam associados).",
      });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao associar",
        description: error.message || "Não foi possível associar os sub-parceiros.",
        variant: "destructive",
      });
    },
  });

  const toggle = (id: string, checked: boolean) => {
    setSelectedIds((prev) =>
      checked ? [...prev, id] : prev.filter((x) => x !== id),
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Associar sub-parceiro</DialogTitle>
          <DialogDescription>
            Selecione sub-parceiros já cadastrados para vincular a este projeto,
            sem editar o projeto inteiro.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Input
            placeholder="Buscar por nome ou documento..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <div className="max-h-64 overflow-y-auto rounded-md border">
            {isLoading ? (
              <p className="p-4 text-sm text-muted-foreground">Carregando...</p>
            ) : available.length === 0 ? (
              <p className="p-4 text-sm text-muted-foreground">
                Nenhum sub-parceiro disponível para associar.
              </p>
            ) : (
              <ul className="divide-y">
                {available.map((sp) => {
                  const checked = selectedIds.includes(sp.id);
                  return (
                    <li
                      key={sp.id}
                      className="flex items-center gap-3 px-3 py-2 hover:bg-muted/40"
                    >
                      <Checkbox
                        id={`assoc-sp-${sp.id}`}
                        checked={checked}
                        onCheckedChange={(value) =>
                          toggle(sp.id, value === true)
                        }
                      />
                      <label
                        htmlFor={`assoc-sp-${sp.id}`}
                        className="flex-1 cursor-pointer text-sm"
                      >
                        <span className="font-medium">{sp.name}</span>
                        {(sp.cnpj || (sp as { cpf?: string }).cpf) && (
                          <span className="ml-2 text-muted-foreground">
                            {sp.cnpj || (sp as { cpf?: string }).cpf}
                          </span>
                        )}
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <Button variant="link" className="h-auto p-0" asChild>
            <Link to={`/clients/${clientId}/sub-partners/new`}>
              <UserPlus className="mr-1 h-4 w-4" />
              Cadastrar novo sub-parceiro
            </Link>
          </Button>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            disabled={selectedIds.length === 0 || mutation.isPending}
            onClick={() => mutation.mutate(selectedIds)}
          >
            {mutation.isPending ? "Associando..." : "Associar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
